import rutasRepartoService from '@/services/rutasRepartoService';
import { mensajeErrorRecorrido } from '@/utils/recorridoRuta';

/**
 * Store module for the delivery-routes screen (`/rutas-reparto`, Trello card
 * #404 "Rutas del día"). Follows the gold-reference shape (`customers.js`):
 * namespaced, async/await actions, explicit loading/error state per concern
 * (the routes list, the active/edited route, and the "pedidos disponibles"
 * panel don't share a single loading flag so one fetch doesn't blank another
 * section while it's in flight).
 */

const MENSAJES_ERROR = {
  PEDIDO_EN_OTRA_RUTA: 'Algunos pedidos ya están en otra ruta',
  PARADA_YA_ENTREGADA: 'Esta parada ya fue marcada como entregada',
  // Seguimiento #416 "Rutas despachables" — reasignar repartidor: sólo se
  // permite en armada/enviada, el backend responde 409 con este code una vez
  // que la ruta ya está en_reparto (o más adelante).
  RUTA_YA_INICIADA: 'La ruta ya inició el reparto — no se puede reasignar el repartidor',
};

const extraerMensajeError = (error, mensajePorDefecto) => {
  const data = error?.response?.data;
  const code = data?.code || data?.error;
  if (code && MENSAJES_ERROR[code]) {
    return MENSAJES_ERROR[code];
  }
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => e.msg || e.message).join(', ');
  }
  return data?.message || data?.msg || error?.message || mensajePorDefecto;
};

// Contrato Optimizar/Recorrido (card #414): errores con forma
// `{ error: true, code, message }` — el `code` siempre manda sobre cualquier
// `message` del backend (ver utils/recorridoRuta.mensajeErrorRecorrido, misma
// tabla que usa el resto de "Rutas del día" para mostrar el aviso).
const extraerMensajeErrorRecorrido = (error, mensajePorDefecto) => {
  const data = error?.response?.data;
  if (data?.code) return mensajeErrorRecorrido(data.code, mensajePorDefecto);
  return data?.message || error?.message || mensajePorDefecto;
};

const state = () => ({
  rutas: [],
  loading: false,
  error: null,

  pedidosDisponibles: [],
  pedidosDisponiblesLoading: false,
  pedidosDisponiblesError: null,
  // Card #414 seguimiento: guarda de secuencia contra reordering de red — sólo
  // la respuesta del último `fetchPedidosDisponibles` en curso puede pisar la
  // lista visible (mismo problema que ya cubría `cargaPedidosSeq` en el
  // componente para `seleccionados`, pero ahí no alcanzaba a proteger el
  // propio commit del store).
  pedidosDisponiblesSeq: 0,

  repartidores: [],
  repartidoresLoading: false,
  repartidoresError: null,

  rutaActual: null,
  rutaActualLoading: false,
  rutaActualError: null,

  guardando: false,
  guardandoError: null,

  distanciaEstimada: null,

  cambiandoEstado: false,
  cambiandoEstadoError: null,

  generandoPdf: false,
  generandoPdfError: null,

  // Card #414 "Rutas del día": recorrido optimizado/calculado (Google Routes,
  // resuelto por el backend) de la ruta seleccionada.
  recorrido: null,
  recorridoLoading: false,
  recorridoError: null,

  optimizando: false,
  optimizandoError: null,

  // Card #416 "Rutas despachables" — despachar/iniciar/paradas. `rutaDetalle`
  // es DISTINTO de `rutaActual` (que usa EditorRutaDialog para crear/editar
  // pedidos): comparten store pero no slot, para que abrir "Editar pedidos"
  // de otra ruta no pise los datos de la ruta que se está siguiendo en el
  // panel de "Rutas del día" (paradas con estado, progreso, etc.).
  rutaDetalle: null,
  rutaDetalleLoading: false,
  rutaDetalleError: null,

  enviando: false,
  enviandoError: null,

  iniciando: false,
  iniciandoError: null,
  // Resultado del último POST iniciar: { movidos, omitidos, fallidos } — la
  // operación no es atómica, así que la vista necesita mostrar qué pedidos
  // NO se movieron (ver RutasReparto.vue).
  resultadoIniciar: null,

  // id_pedido de la parada que se está marcando (entregado/no entregado) —
  // deshabilita sus botones mientras está en curso.
  marcandoParadaId: null,
  marcandoParadaError: null,

  // Seguimiento #416 "Rutas despachables": reasignar repartidor sin cancelar
  // la ruta (armada/enviada; 409 RUTA_YA_INICIADA en en_reparto+).
  reasignando: false,
  reasignandoError: null,

  misRutas: [],
  misRutasLoading: false,
  misRutasError: null,
});

const getters = {
  mensajeError: () => (code, mensajePorDefecto = 'Error') => MENSAJES_ERROR[code] || mensajePorDefecto,
};

const actions = {
  async fetchRutas({ commit }, filtros = {}) {
    try {
      commit('SET_LOADING', true);
      const { data } = await rutasRepartoService.obtenerRutas(filtros);
      commit('SET_RUTAS', data.rutas || []);
      commit('SET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', extraerMensajeError(error, 'Error al cargar las rutas'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchPedidosDisponibles({ commit, state }, filtros = {}) {
    const seq = state.pedidosDisponiblesSeq + 1;
    commit('SET_PEDIDOS_DISPONIBLES_SEQ', seq);
    try {
      commit('SET_PEDIDOS_DISPONIBLES_LOADING', true);
      const { data } = await rutasRepartoService.obtenerPedidosDisponibles(filtros);
      // Sólo aplica la respuesta si sigue siendo la más nueva en curso — si
      // una llamada posterior ya arrancó, esta quedó vieja aunque resuelva
      // después (network reordering) y no debe pisar la lista visible.
      if (seq === state.pedidosDisponiblesSeq) {
        commit('SET_PEDIDOS_DISPONIBLES', data.pedidos || []);
      }
      commit('SET_PEDIDOS_DISPONIBLES_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', extraerMensajeError(error, 'Error al cargar los pedidos disponibles'));
      commit('SET_PEDIDOS_DISPONIBLES_ERROR', extraerMensajeError(error, 'Error al cargar los pedidos disponibles'));
      throw error;
    } finally {
      commit('SET_PEDIDOS_DISPONIBLES_LOADING', false);
    }
  },

  /** Repartidores = usuarios con `id_rol === 7` (misma fuente que AdminVentas/DialogRepartidor). */
  async fetchRepartidores({ commit }) {
    try {
      commit('SET_REPARTIDORES_LOADING', true);
      const { data } = await rutasRepartoService.obtenerRepartidores();
      const repartidores = (data || []).filter((usuario) => usuario.id_rol === 7);
      commit('SET_REPARTIDORES', repartidores);
      commit('SET_REPARTIDORES_ERROR', null);
      return repartidores;
    } catch (error) {
      commit('SET_REPARTIDORES_ERROR', extraerMensajeError(error, 'Error al cargar los repartidores'));
      throw error;
    } finally {
      commit('SET_REPARTIDORES_LOADING', false);
    }
  },

  async fetchRuta({ commit }, idRuta) {
    try {
      commit('SET_RUTA_ACTUAL_LOADING', true);
      const { data } = await rutasRepartoService.obtenerRuta(idRuta);
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_RUTA_ACTUAL_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_RUTA_ACTUAL_ERROR', extraerMensajeError(error, 'Error al cargar la ruta'));
      throw error;
    } finally {
      commit('SET_RUTA_ACTUAL_LOADING', false);
    }
  },

  async crearRuta({ commit }, payload) {
    try {
      commit('SET_GUARDANDO', true);
      const { data } = await rutasRepartoService.crearRuta(payload);
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_GUARDANDO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_GUARDANDO_ERROR', extraerMensajeError(error, 'Error al crear la ruta'));
      throw error;
    } finally {
      commit('SET_GUARDANDO', false);
    }
  },

  async actualizarPedidosRuta({ commit }, { idRuta, agregar = [], quitar = [] }) {
    try {
      commit('SET_GUARDANDO', true);
      const { data } = await rutasRepartoService.actualizarPedidos(idRuta, { agregar, quitar });
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_GUARDANDO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_GUARDANDO_ERROR', extraerMensajeError(error, 'Error al actualizar los pedidos de la ruta'));
      throw error;
    } finally {
      commit('SET_GUARDANDO', false);
    }
  },

  async actualizarOrdenRuta({ commit }, { idRuta, pedidos }) {
    try {
      commit('SET_GUARDANDO', true);
      const { data } = await rutasRepartoService.actualizarOrden(idRuta, pedidos);
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_GUARDANDO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_GUARDANDO_ERROR', extraerMensajeError(error, 'Error al guardar el orden de la ruta'));
      throw error;
    } finally {
      commit('SET_GUARDANDO', false);
    }
  },

  async ordenarRutaPorCercania({ commit }, idRuta) {
    try {
      commit('SET_GUARDANDO', true);
      const { data } = await rutasRepartoService.ordenarPorCercania(idRuta);
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_DISTANCIA_ESTIMADA', data.ruta?.distancia_estimada_km ?? null);
      commit('SET_GUARDANDO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_GUARDANDO_ERROR', extraerMensajeError(error, 'Error al ordenar la ruta por cercanía'));
      throw error;
    } finally {
      commit('SET_GUARDANDO', false);
    }
  },

  async cambiarEstadoRuta({ commit }, { idRuta, estado }) {
    try {
      commit('SET_CAMBIANDO_ESTADO', true);
      const { data } = await rutasRepartoService.actualizarEstado(idRuta, estado);
      commit('SET_RUTA_ACTUAL', data.ruta);
      commit('SET_CAMBIANDO_ESTADO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_CAMBIANDO_ESTADO_ERROR', extraerMensajeError(error, 'Error al cambiar el estado de la ruta'));
      throw error;
    } finally {
      commit('SET_CAMBIANDO_ESTADO', false);
    }
  },

  async imprimirRuta({ commit }, idRuta) {
    try {
      commit('SET_GENERANDO_PDF', true);
      const { data } = await rutasRepartoService.obtenerPdf(idRuta);
      commit('SET_GENERANDO_PDF_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_GENERANDO_PDF_ERROR', extraerMensajeError(error, 'Error al generar la hoja de ruta'));
      throw error;
    } finally {
      commit('SET_GENERANDO_PDF', false);
    }
  },

  limpiarRutaActual({ commit }) {
    commit('SET_RUTA_ACTUAL', null);
    commit('SET_DISTANCIA_ESTIMADA', null);
    commit('SET_GUARDANDO_ERROR', null);
  },

  // Card #414 "Rutas del día" — optimización con Google/Recorrido.

  async fetchRecorrido({ commit }, { idRuta, volverAlCentro }) {
    try {
      commit('SET_RECORRIDO_LOADING', true);
      const { data } = await rutasRepartoService.obtenerRecorrido(idRuta, { volver_al_centro: volverAlCentro });
      commit('SET_RECORRIDO', data.recorrido);
      commit('SET_RECORRIDO_ERROR', null);
      return data.recorrido;
    } catch (error) {
      commit('SET_RECORRIDO_ERROR', extraerMensajeErrorRecorrido(error, 'Error al cargar el recorrido'));
      throw error;
    } finally {
      commit('SET_RECORRIDO_LOADING', false);
    }
  },

  async optimizarRuta({ commit }, { idRuta, volverAlCentro, idCentro }) {
    try {
      commit('SET_OPTIMIZANDO', true);
      const { data } = await rutasRepartoService.optimizarRuta(idRuta, {
        volver_al_centro: volverAlCentro,
        id_centro: idCentro,
        aplicar: true,
      });
      commit('SET_RECORRIDO', data.recorrido);
      commit('SET_OPTIMIZANDO_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_OPTIMIZANDO_ERROR', extraerMensajeErrorRecorrido(error, 'Error al optimizar el recorrido'));
      throw error;
    } finally {
      commit('SET_OPTIMIZANDO', false);
    }
  },

  // ─── Card #416 "Rutas despachables" ────────────────────────────────────

  /** Carga la ruta con el detalle de paradas (pedidos + estado) en `rutaDetalle`. */
  async fetchRutaDetalle({ commit }, idRuta) {
    try {
      commit('SET_RUTA_DETALLE_LOADING', true);
      const { data } = await rutasRepartoService.obtenerRuta(idRuta);
      commit('SET_RUTA_DETALLE', data.ruta);
      commit('SET_RUTA_DETALLE_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_RUTA_DETALLE_ERROR', extraerMensajeError(error, 'Error al cargar el detalle de la ruta'));
      throw error;
    } finally {
      commit('SET_RUTA_DETALLE_LOADING', false);
    }
  },

  // El contrato no confirma si la respuesta trae `ruta` — si no viene, se
  // refresca con un GET aparte para no dejar el store desactualizado.
  async enviarRuta({ commit, dispatch }, idRuta) {
    try {
      commit('SET_ENVIANDO', true);
      const { data } = await rutasRepartoService.enviarRuta(idRuta);
      const ruta = data?.ruta || (await dispatch('fetchRutaDetalle', idRuta));
      commit('SET_RUTA_DETALLE', ruta);
      commit('UPDATE_RUTA_EN_LISTA', ruta);
      commit('SET_ENVIANDO_ERROR', null);
      return ruta;
    } catch (error) {
      commit('SET_ENVIANDO_ERROR', extraerMensajeError(error, 'Error al enviar la ruta al repartidor'));
      throw error;
    } finally {
      commit('SET_ENVIANDO', false);
    }
  },

  // No atómica (ver backend): guarda movidos/omitidos/fallidos en
  // `resultadoIniciar` para que la vista pueda avisar qué pedidos no se
  // movieron, además de refrescar la ruta.
  async iniciarRuta({ commit, dispatch }, idRuta) {
    try {
      commit('SET_INICIANDO', true);
      const { data } = await rutasRepartoService.iniciarRuta(idRuta);
      const resultado = {
        movidos: data?.movidos || [],
        omitidos: data?.omitidos || [],
        fallidos: data?.fallidos || [],
      };
      commit('SET_RESULTADO_INICIAR', resultado);
      const ruta = data?.ruta || (await dispatch('fetchRutaDetalle', idRuta));
      commit('SET_RUTA_DETALLE', ruta);
      commit('UPDATE_RUTA_EN_LISTA', ruta);
      commit('SET_INICIANDO_ERROR', null);
      return { ruta, ...resultado };
    } catch (error) {
      commit('SET_INICIANDO_ERROR', extraerMensajeError(error, 'Error al iniciar el reparto'));
      throw error;
    } finally {
      commit('SET_INICIANDO', false);
    }
  },

  limpiarResultadoIniciar({ commit }) {
    commit('SET_RESULTADO_INICIAR', null);
  },

  // estado: 'entregado' | 'no_entregado'. motivoNoEntrega requerido por el
  // backend solo para 'no_entregado' (la vista ya lo exige antes de llamar).
  async marcarParada({ commit, dispatch }, { idRuta, idPedido, estado, motivoNoEntrega }) {
    try {
      commit('SET_MARCANDO_PARADA_ID', idPedido);
      const { data } = await rutasRepartoService.marcarParada(idRuta, idPedido, {
        estado,
        motivo_no_entrega: motivoNoEntrega,
      });
      const ruta = data?.ruta || (await dispatch('fetchRutaDetalle', idRuta));
      commit('SET_RUTA_DETALLE', ruta);
      commit('UPDATE_RUTA_EN_LISTA', ruta);
      commit('SET_MARCANDO_PARADA_ERROR', null);
      return ruta;
    } catch (error) {
      commit('SET_MARCANDO_PARADA_ERROR', extraerMensajeError(error, 'Error al actualizar la parada'));
      throw error;
    } finally {
      commit('SET_MARCANDO_PARADA_ID', null);
    }
  },

  limpiarRutaDetalle({ commit }) {
    commit('SET_RUTA_DETALLE', null);
    commit('SET_RESULTADO_INICIAR', null);
  },

  // Seguimiento #416 "Rutas despachables": reasigna el repartidor sin
  // cancelar la ruta. A diferencia de enviarRuta/iniciarRuta, el contrato del
  // backend SIEMPRE devuelve `ruta` en la respuesta — no hace falta fallback
  // a fetchRutaDetalle.
  async reasignarRepartidor({ commit }, { idRuta, idRepartidor }) {
    try {
      commit('SET_REASIGNANDO', true);
      const { data } = await rutasRepartoService.reasignarRepartidor(idRuta, idRepartidor);
      commit('SET_RUTA_DETALLE', data.ruta);
      commit('UPDATE_RUTA_EN_LISTA', data.ruta);
      commit('SET_REASIGNANDO_ERROR', null);
      return data.ruta;
    } catch (error) {
      commit('SET_REASIGNANDO_ERROR', extraerMensajeError(error, 'Error al reasignar el repartidor'));
      throw error;
    } finally {
      commit('SET_REASIGNANDO', false);
    }
  },

  // Refresco silencioso disparado por los eventos de socket (rutaEnviada/
  // rutaIniciada/rutaActualizada): sólo toca la tarjeta de esa ruta en
  // `rutas` — NUNCA `rutaDetalle`, para no pisar el detalle de la ruta que
  // el usuario tiene abierta en el panel si el evento es de otra ruta (la
  // vista decide aparte si además hay que refrescar `rutaDetalle`).
  async refrescarRutaEnLista({ commit }, idRuta) {
    const { data } = await rutasRepartoService.obtenerRuta(idRuta);
    commit('UPDATE_RUTA_EN_LISTA', data.ruta);
    return data.ruta;
  },

  async fetchMisRutas({ commit }, filtros = {}) {
    try {
      commit('SET_MIS_RUTAS_LOADING', true);
      const { data } = await rutasRepartoService.obtenerMisRutas(filtros);
      commit('SET_MIS_RUTAS', data.rutas || []);
      commit('SET_MIS_RUTAS_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_MIS_RUTAS_ERROR', extraerMensajeError(error, 'Error al cargar mis rutas'));
      throw error;
    } finally {
      commit('SET_MIS_RUTAS_LOADING', false);
    }
  },
};

const mutations = {
  SET_RUTAS(s, rutas) {
    s.rutas = rutas;
  },
  SET_LOADING(s, v) {
    s.loading = v;
  },
  SET_ERROR(s, v) {
    s.error = v;
  },

  SET_PEDIDOS_DISPONIBLES(s, pedidos) {
    s.pedidosDisponibles = pedidos;
  },
  SET_PEDIDOS_DISPONIBLES_LOADING(s, v) {
    s.pedidosDisponiblesLoading = v;
  },
  SET_PEDIDOS_DISPONIBLES_ERROR(s, v) {
    s.pedidosDisponiblesError = v;
  },
  SET_PEDIDOS_DISPONIBLES_SEQ(s, seq) {
    s.pedidosDisponiblesSeq = seq;
  },

  SET_REPARTIDORES(s, repartidores) {
    s.repartidores = repartidores;
  },
  SET_REPARTIDORES_LOADING(s, v) {
    s.repartidoresLoading = v;
  },
  SET_REPARTIDORES_ERROR(s, v) {
    s.repartidoresError = v;
  },

  SET_RUTA_ACTUAL(s, ruta) {
    s.rutaActual = ruta;
  },
  SET_RUTA_ACTUAL_LOADING(s, v) {
    s.rutaActualLoading = v;
  },
  SET_RUTA_ACTUAL_ERROR(s, v) {
    s.rutaActualError = v;
  },

  SET_GUARDANDO(s, v) {
    s.guardando = v;
  },
  SET_GUARDANDO_ERROR(s, v) {
    s.guardandoError = v;
  },

  SET_DISTANCIA_ESTIMADA(s, v) {
    s.distanciaEstimada = v;
  },

  SET_CAMBIANDO_ESTADO(s, v) {
    s.cambiandoEstado = v;
  },
  SET_CAMBIANDO_ESTADO_ERROR(s, v) {
    s.cambiandoEstadoError = v;
  },

  SET_GENERANDO_PDF(s, v) {
    s.generandoPdf = v;
  },
  SET_GENERANDO_PDF_ERROR(s, v) {
    s.generandoPdfError = v;
  },

  SET_RECORRIDO(s, v) {
    s.recorrido = v;
  },
  SET_RECORRIDO_LOADING(s, v) {
    s.recorridoLoading = v;
  },
  SET_RECORRIDO_ERROR(s, v) {
    s.recorridoError = v;
  },

  SET_OPTIMIZANDO(s, v) {
    s.optimizando = v;
  },
  SET_OPTIMIZANDO_ERROR(s, v) {
    s.optimizandoError = v;
  },

  // ─── Card #416 "Rutas despachables" ────────────────────────────────────

  SET_RUTA_DETALLE(s, v) {
    s.rutaDetalle = v;
  },
  SET_RUTA_DETALLE_LOADING(s, v) {
    s.rutaDetalleLoading = v;
  },
  SET_RUTA_DETALLE_ERROR(s, v) {
    s.rutaDetalleError = v;
  },

  SET_ENVIANDO(s, v) {
    s.enviando = v;
  },
  SET_ENVIANDO_ERROR(s, v) {
    s.enviandoError = v;
  },

  SET_INICIANDO(s, v) {
    s.iniciando = v;
  },
  SET_INICIANDO_ERROR(s, v) {
    s.iniciandoError = v;
  },
  SET_RESULTADO_INICIAR(s, v) {
    s.resultadoIniciar = v;
  },

  SET_MARCANDO_PARADA_ID(s, v) {
    s.marcandoParadaId = v;
  },
  SET_MARCANDO_PARADA_ERROR(s, v) {
    s.marcandoParadaError = v;
  },

  SET_REASIGNANDO(s, v) {
    s.reasignando = v;
  },
  SET_REASIGNANDO_ERROR(s, v) {
    s.reasignandoError = v;
  },

  // Reemplaza, dentro de `rutas` (el listado del día), la entrada cuyo
  // id_ruta coincide — así ListaRutas refleja el chip/timestamps nuevos sin
  // esperar un fetchRutas completo. No hace nada si la ruta no está listada
  // (ej. quedó fuera de filtro).
  UPDATE_RUTA_EN_LISTA(s, ruta) {
    if (!ruta || ruta.id_ruta == null) return;
    const index = s.rutas.findIndex((r) => r.id_ruta === ruta.id_ruta);
    if (index === -1) return;
    s.rutas.splice(index, 1, ruta);
  },

  SET_MIS_RUTAS(s, v) {
    s.misRutas = v;
  },
  SET_MIS_RUTAS_LOADING(s, v) {
    s.misRutasLoading = v;
  },
  SET_MIS_RUTAS_ERROR(s, v) {
    s.misRutasError = v;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
